// ── Bilingual strings for the WhatsApp-style business registration chat ──
// Every bot message and UI label lives here so the chat can switch between
// Malayalam and English without touching the flow logic.

const STRINGS = {
  en: {
    headerName: "Master Click",
    headerStatus: "online",
    today: "TODAY",
    typing: "typing…",

    greet: "Hi 👋 Welcome to Master Click.",
    greet2:
      "I'll help you list your business — just a few quick questions. Ready?",

    askOwnerName: "First, what's your name?",
    askMobile: "Your mobile number? (10 digits)",
    sendingOtp: "Sending you a verification code…",
    otpSent: (m) => `I've sent a 4-digit code to ${m}. Please enter it below.`,
    otpWrong: "That code doesn't match. Please try again 🙂",
    otpResent: "New code sent.",
    resend: "Resend code",
    changeNumber: "Change number",
    verified: "Verified ✅",

    alreadyHasBusiness:
      "This number already has a business registered with us. To list another one, please use a different mobile number.",
    viewMyListing: "View my listing",

    askBizName: "Great! What's your business name?",
    askType: "Is it a Shop or a Service?",
    typeShop: "Shop",
    typeService: "Service",
    askCategory: "Which category does it belong to?",
    askSubCategory: "And the sub category?",
    askLocation:
      "Now your location 📍 — share your current location, or search for the place.",
    shareLocation: "Share my current location",
    searchPlace: "Search a place",
    locating: "Getting your location…",
    locationDenied:
      "I couldn't read your location. Please search for the place instead.",
    locationSet: (n) => `Location set${n ? ` — ${n}` : ""} ✅`,
    askDistrict: "Which district?",
    districtGuess: (n) => `Looks like ${n}. Is that right?`,
    askPostOffice: "Your city / post office? (optional)",
    askPincode: "Pincode? (optional)",
    askAbout: "Tell customers a little about your business. (optional)",
    askLogo: "Send your shop logo or profile photo. (optional)",
    askBanner: "Now a wide banner image for the top of your page. (optional)",

    askCatalogue:
      "Would you like to add product photos to your catalogue? (optional)",
    askCatalogueTitle: "Nice! What is this item called?",
    askCatalogueMore: "Add another product photo?",
    catalogueLimit: (n) =>
      `That's the maximum of ${n} catalogue photos on your free plan.`,

    askCover: "Any gallery / cover photos of your place? (optional)",
    askCoverMore: "Add another cover photo?",
    coverLimit: (n) =>
      `That's the maximum of ${n} cover photos on your free plan.`,

    askServices: "What services do you offer? Send them one by one. (optional)",
    askServicesMore: "Add another service?",
    serviceLimit: (n) => `That's the maximum of ${n} services on your free plan.`,

    askSocial: "Want to add your social media and website links? (optional)",
    askWhatsapp: "WhatsApp number?",
    askFacebook: "Facebook page link?",
    askInstagram: "Instagram link?",
    askYoutube: "YouTube channel link?",
    askWebsite: "Website link?",
    askGoogleReview: "Google review link?",
    askGoogleLocation: "Google Maps location link?",

    reviewIntro: "That's everything! Please check your details before I submit:",
    submit: "Submit registration",
    submitting: "Submitting…",
    submitFailed: (m) => `Couldn't submit: ${m || "please try again"}`,

    doneTitle: "Done! 🎉 Your business has been submitted.",
    doneBody:
      "Our team will review and activate your listing shortly. You can preview your page below.",
    previewBtn: "Preview my business",
    myListingsBtn: "Go to My Listings",

    yes: "Yes",
    no: "No",
    skip: "Skip",
    done: "Done",
    add: "Add",
    edit: "Edit",
    send: "Send",
    addPhoto: "Add photo",
    uploading: "Uploading…",
    search: "Search…",
    noResults: "Nothing found.",
    typeHere: "Type here…",
    retry: "Retry",
    sendPhoto: "Send the photo 📷",
    sessionExpired:
      "I lost your session. Please confirm your mobile number again to continue.",

    errName: "Please tell me your name.",
    errBizName: "Please enter your business name.",
    errMobile: "Numbers only — please send all 10 digits.",
    errOtp: "The code is 4 digits.",
    errPincode: "Pincode should be 6 digits.",
    errLink: "That doesn't look like a valid link.",
    errImage: "Please send an image under 5 MB.",
    errTooLong: "That's a bit too long — please keep it shorter.",
    errRequired: "This one is required 🙂",
    errNetwork: "Something went wrong. Please try again.",

    /* ── Upgrade chat (/upgrade-chat) ── */
    upgradeGreet: "Hi 👋 Let's get you a bigger plan.",
    loadingPlans: "Fetching the available plans…",
    plansFailed: "I couldn't load the plans. Please try again.",
    currentPlanIs: (n) => `You're on ${n} right now.`,
    noCurrentPlan: "You don't have an active plan yet.",
    plansIntro: "Here's what you can move to:",
    planAdds: "Extra with this plan:",
    planNothingExtra: "Same features as your current plan.",
    noPlans: "There's nothing to upgrade to at the moment.",
    choose: "Choose",
    confirmPay: (name, amount) => `${name} — ₹${amount}. Shall I open the payment?`,
    payNow: "Pay now",
    cancel: "Cancel",
    openingPayment: "Opening the payment window…",
    paymentCancelled: "Payment cancelled. Nothing was charged.",
    paymentFailed: (m) => `Payment failed: ${m || "please try again"}`,
    paymentOk: "Payment successful ✅",
    activating: "Activating your plan… this takes a moment.",
    activated: (n) => `You're on ${n} now 🎉`,
    activationSlow:
      "Payment received ✅ Your plan will switch on within a few minutes. Come back and add the new details after that.",
    unlockedIntro: "This plan unlocks:",
    unlockedCount: (n, what) => `${n} more ${what}`,
    unlockedNothing: "Nothing new to fill in — you're all set.",
    fillNow: "Add them now",
    later: "Later",
    upgradeBtn: "Upgrade my plan",
    /* ── Filling in newly unlocked fields, handed over from the upgrade chat ── */
    focusIntro: "Let's fill in what your new plan unlocked.",
    focusDone: "That's everything new. Anything else to change?",

    labels: {
      ownerName: "Your name",
      mobile: "Mobile",
      bizName: "Business name",
      type: "Type",
      category: "Category",
      subCategory: "Sub category",
      location: "Location",
      district: "District",
      postOffice: "City / Post office",
      pincode: "Pincode",
      about: "About",
      logo: "Logo",
      banner: "Banner",
      catalogue: "Catalogue photos",
      cover: "Cover photos",
      services: "Services",
      social: "Social links",
    },

    /* ── Edit chat (/edit-chat) ── */
    editGreet: "Hi 👋 Let's update your listing.",
    loadingBiz: "Fetching your business details…",
    loadFailed: "I couldn't load your business. Please try again.",
    noAuth: "Please sign in first, then I can open your listing.",
    signInBtn: "Sign in",
    noBusiness: "You haven't listed a business yet. Shall we create one?",
    registerBtn: "Register my business",
    menuIntro: "Here's your listing. Tap Edit on anything you'd like to change.",
    menuAgain: "Anything else you'd like to change?",
    current: (v) => `Right now it's: ${v}`,
    currentNone: "You haven't set this yet.",
    saved: "Saved ✅",
    saveFailed: (m) => `Couldn't save: ${m || "please try again"}`,
    back: "Back to the list",
    finish: "I'm done",
    planNote:
      "Some fields aren't part of your current plan, so they're not listed here.",
    catalogueMenuIntro: "Your catalogue photos:",
    coverMenuIntro: "Your cover photos:",
    serviceMenuIntro: "Your services:",
    socialMenuIntro: "Your links:",
    emptyList: "Nothing added yet.",
    addNew: "Add new",
    remove: "Remove",
    removed: "Removed ✅",
    confirmRemove: (n) => `Remove ${n ? `"${n}"` : "this"}?`,
    linkCannotClear:
      "A link can't be left empty — send me the new one, or go back.",
    editDoneTitle: "All updated 🎉",
    editDoneBody: "Your changes are live on your business page.",
    socialLabels: {
      whatsapp_number: "WhatsApp",
      facebook: "Facebook",
      instagram: "Instagram",
      youtube: "YouTube",
      website: "Website",
      google_review_link: "Google review",
      google_location_link: "Google Maps",
    },
  },

  ml: {
    headerName: "മാസ്റ്റർ ക്ലിക്ക്",
    headerStatus: "ഓൺലൈൻ",
    today: "ഇന്ന്",
    typing: "ടൈപ്പ് ചെയ്യുന്നു…",

    greet: "ഹായ് 👋 മാസ്റ്റർ ക്ലിക്കിലേക്ക് സ്വാഗതം.",
    greet2:
      "നിങ്ങളുടെ സ്ഥാപനം ചേർക്കാൻ ഞാൻ സഹായിക്കാം — കുറച്ച് ചോദ്യങ്ങൾ മാത്രം. തുടങ്ങാം?",

    askOwnerName: "ആദ്യം, നിങ്ങളുടെ പേര് എന്താണ്?",
    askMobile: "നിങ്ങളുടെ മൊബൈൽ നമ്പർ? (10 അക്കം)",
    sendingOtp: "വെരിഫിക്കേഷൻ കോഡ് അയയ്ക്കുന്നു…",
    otpSent: (m) =>
      `${m} എന്ന നമ്പറിലേക്ക് 4 അക്ക കോഡ് അയച്ചിട്ടുണ്ട്. താഴെ നൽകുക.`,
    otpWrong: "കോഡ് ശരിയല്ല. ഒന്നുകൂടി ശ്രമിക്കൂ 🙂",
    otpResent: "പുതിയ കോഡ് അയച്ചു.",
    resend: "കോഡ് വീണ്ടും അയയ്ക്കുക",
    changeNumber: "നമ്പർ മാറ്റുക",
    verified: "വെരിഫൈ ചെയ്തു ✅",

    alreadyHasBusiness:
      "ഈ നമ്പറിൽ ഇതിനകം ഒരു സ്ഥാപനം രജിസ്റ്റർ ചെയ്തിട്ടുണ്ട്. മറ്റൊന്ന് ചേർക്കാൻ വേറെ മൊബൈൽ നമ്പർ ഉപയോഗിക്കുക.",
    viewMyListing: "എന്റെ ലിസ്റ്റിംഗ് കാണുക",

    askBizName: "കൊള്ളാം! സ്ഥാപനത്തിന്റെ പേര് എന്താണ്?",
    askType: "ഇത് ഒരു കടയാണോ അതോ സേവനമാണോ?",
    typeShop: "കട",
    typeService: "സേവനം",
    askCategory: "ഏത് വിഭാഗത്തിൽ വരും?",
    askSubCategory: "ഉപവിഭാഗം ഏതാണ്?",
    askLocation:
      "ഇനി ലൊക്കേഷൻ 📍 — നിലവിലുള്ള സ്ഥലം ഷെയർ ചെയ്യുക, അല്ലെങ്കിൽ സ്ഥലം തിരയുക.",
    shareLocation: "എന്റെ ലൊക്കേഷൻ ഷെയർ ചെയ്യുക",
    searchPlace: "സ്ഥലം തിരയുക",
    locating: "ലൊക്കേഷൻ എടുക്കുന്നു…",
    locationDenied:
      "ലൊക്കേഷൻ ലഭിച്ചില്ല. ദയവായി സ്ഥലം തിരഞ്ഞ് തിരഞ്ഞെടുക്കുക.",
    locationSet: (n) => `ലൊക്കേഷൻ സേവ് ചെയ്തു${n ? ` — ${n}` : ""} ✅`,
    askDistrict: "ഏത് ജില്ല?",
    districtGuess: (n) => `${n} ആണെന്ന് തോന്നുന്നു. ശരിയാണോ?`,
    askPostOffice: "നിങ്ങളുടെ സിറ്റി / പോസ്റ്റ് ഓഫീസ്? (നിർബന്ധമല്ല)",
    askPincode: "പിൻകോഡ്? (നിർബന്ധമല്ല)",
    askAbout:
      "സ്ഥാപനത്തെക്കുറിച്ച് ഉപഭോക്താക്കൾക്ക് ഒരു ചെറിയ വിവരണം. (നിർബന്ധമല്ല)",
    askLogo: "കടയുടെ ലോഗോ അല്ലെങ്കിൽ പ്രൊഫൈൽ ഫോട്ടോ അയയ്ക്കുക. (നിർബന്ധമല്ല)",
    askBanner: "പേജിന്റെ മുകളിൽ വരാൻ ഒരു വൈഡ് ബാനർ ചിത്രം. (നിർബന്ധമല്ല)",

    askCatalogue:
      "കാറ്റലോഗിലേക്ക് ഉൽപ്പന്ന ഫോട്ടോകൾ ചേർക്കണോ? (നിർബന്ധമല്ല)",
    askCatalogueTitle: "കൊള്ളാം! ഈ ഉൽപ്പന്നത്തിന്റെ പേര് എന്താണ്?",
    askCatalogueMore: "ഒരു ഫോട്ടോ കൂടി ചേർക്കണോ?",
    catalogueLimit: (n) =>
      `ഫ്രീ പ്ലാനിൽ പരമാവധി ${n} കാറ്റലോഗ് ഫോട്ടോകൾ മാത്രം.`,

    askCover: "സ്ഥാപനത്തിന്റെ ഗാലറി / കവർ ഫോട്ടോകൾ ഉണ്ടോ? (നിർബന്ധമല്ല)",
    askCoverMore: "ഒരു കവർ ഫോട്ടോ കൂടി ചേർക്കണോ?",
    coverLimit: (n) => `ഫ്രീ പ്ലാനിൽ പരമാവധി ${n} കവർ ഫോട്ടോകൾ മാത്രം.`,

    askServices:
      "നിങ്ങൾ നൽകുന്ന സേവനങ്ങൾ ഏതൊക്കെയാണ്? ഓരോന്നായി അയയ്ക്കുക. (നിർബന്ധമല്ല)",
    askServicesMore: "ഒരു സേവനം കൂടി ചേർക്കണോ?",
    serviceLimit: (n) => `ഫ്രീ പ്ലാനിൽ പരമാവധി ${n} സേവനങ്ങൾ മാത്രം.`,

    askSocial: "സോഷ്യൽ മീഡിയ, വെബ്സൈറ്റ് ലിങ്കുകൾ ചേർക്കണോ? (നിർബന്ധമല്ല)",
    askWhatsapp: "വാട്സ്ആപ്പ് നമ്പർ?",
    askFacebook: "ഫേസ്ബുക്ക് പേജ് ലിങ്ക്?",
    askInstagram: "ഇൻസ്റ്റാഗ്രാം ലിങ്ക്?",
    askYoutube: "യൂട്യൂബ് ചാനൽ ലിങ്ക്?",
    askWebsite: "വെബ്സൈറ്റ് ലിങ്ക്?",
    askGoogleReview: "ഗൂഗിൾ റിവ്യൂ ലിങ്ക്?",
    askGoogleLocation: "ഗൂഗിൾ മാപ്സ് ലൊക്കേഷൻ ലിങ്ക്?",

    reviewIntro:
      "എല്ലാം കഴിഞ്ഞു! സബ്മിറ്റ് ചെയ്യുന്നതിന് മുൻപ് വിവരങ്ങൾ പരിശോധിക്കുക:",
    submit: "രജിസ്ട്രേഷൻ സബ്മിറ്റ് ചെയ്യുക",
    submitting: "സബ്മിറ്റ് ചെയ്യുന്നു…",
    submitFailed: (m) => `സബ്മിറ്റ് ചെയ്യാനായില്ല: ${m || "വീണ്ടും ശ്രമിക്കുക"}`,

    doneTitle: "കഴിഞ്ഞു! 🎉 നിങ്ങളുടെ സ്ഥാപനം സബ്മിറ്റ് ചെയ്തു.",
    doneBody:
      "ഞങ്ങളുടെ ടീം പരിശോധിച്ച് ഉടൻ ആക്ടിവേറ്റ് ചെയ്യും. താഴെ നിങ്ങളുടെ പേജ് കാണാം.",
    previewBtn: "എന്റെ സ്ഥാപനം കാണുക",
    myListingsBtn: "എന്റെ ലിസ്റ്റിംഗിലേക്ക്",

    yes: "വേണം",
    no: "വേണ്ട",
    skip: "വേണ്ട",
    done: "കഴിഞ്ഞു",
    add: "ചേർക്കുക",
    edit: "മാറ്റുക",
    send: "അയയ്ക്കുക",
    addPhoto: "ഫോട്ടോ ചേർക്കുക",
    uploading: "അപ്‌ലോഡ് ചെയ്യുന്നു…",
    search: "തിരയുക…",
    noResults: "ഒന്നും കണ്ടെത്തിയില്ല.",
    typeHere: "ഇവിടെ ടൈപ്പ് ചെയ്യുക…",
    retry: "വീണ്ടും ശ്രമിക്കുക",
    sendPhoto: "ഫോട്ടോ അയയ്ക്കുക 📷",
    sessionExpired:
      "സെഷൻ നഷ്ടപ്പെട്ടു. തുടരാൻ മൊബൈൽ നമ്പർ ഒന്നുകൂടി നൽകുക.",

    errName: "ദയവായി നിങ്ങളുടെ പേര് നൽകുക.",
    errBizName: "ദയവായി സ്ഥാപനത്തിന്റെ പേര് നൽകുക.",
    errMobile: "അക്കങ്ങൾ മാത്രം — 10 അക്കം നൽകുക.",
    errOtp: "കോഡ് 4 അക്കമാണ്.",
    errPincode: "പിൻകോഡ് 6 അക്കമായിരിക്കണം.",
    errLink: "ഇത് ശരിയായ ലിങ്ക് ആയി തോന്നുന്നില്ല.",
    errImage: "5 MB-യിൽ താഴെയുള്ള ചിത്രം അയയ്ക്കുക.",
    errTooLong: "അൽപ്പം കൂടി ചുരുക്കി എഴുതാമോ?",
    errRequired: "ഇത് നിർബന്ധമാണ് 🙂",
    errNetwork: "എന്തോ പ്രശ്നം ഉണ്ടായി. വീണ്ടും ശ്രമിക്കുക.",

    /* ── Upgrade chat (/upgrade-chat) ── */
    upgradeGreet: "ഹായ് 👋 വലിയ ഒരു പ്ലാനിലേക്ക് മാറാം.",
    loadingPlans: "ലഭ്യമായ പ്ലാനുകൾ എടുക്കുന്നു…",
    plansFailed: "പ്ലാനുകൾ ലഭിച്ചില്ല. വീണ്ടും ശ്രമിക്കുക.",
    currentPlanIs: (n) => `നിങ്ങൾ ഇപ്പോൾ ${n} പ്ലാനിലാണ്.`,
    noCurrentPlan: "നിങ്ങൾക്ക് ഇപ്പോൾ ആക്ടീവ് പ്ലാൻ ഇല്ല.",
    plansIntro: "ഇവയിലേക്ക് മാറാം:",
    planAdds: "ഈ പ്ലാനിൽ അധികമായി:",
    planNothingExtra: "നിലവിലെ പ്ലാനിലെ അതേ സൗകര്യങ്ങൾ.",
    noPlans: "ഇപ്പോൾ അപ്ഗ്രേഡ് ചെയ്യാൻ പ്ലാനുകളൊന്നുമില്ല.",
    choose: "തിരഞ്ഞെടുക്കുക",
    confirmPay: (name, amount) =>
      `${name} — ₹${amount}. പേയ്മെന്റ് തുറക്കട്ടെ?`,
    payNow: "ഇപ്പോൾ അടയ്ക്കുക",
    cancel: "വേണ്ട",
    openingPayment: "പേയ്മെന്റ് വിൻഡോ തുറക്കുന്നു…",
    paymentCancelled: "പേയ്മെന്റ് റദ്ദാക്കി. പണം ഈടാക്കിയിട്ടില്ല.",
    paymentFailed: (m) => `പേയ്മെന്റ് പരാജയപ്പെട്ടു: ${m || "വീണ്ടും ശ്രമിക്കുക"}`,
    paymentOk: "പേയ്മെന്റ് വിജയകരം ✅",
    activating: "പ്ലാൻ ആക്ടിവേറ്റ് ചെയ്യുന്നു… അൽപ്പം സമയമെടുക്കും.",
    activated: (n) => `ഇപ്പോൾ നിങ്ങൾ ${n} പ്ലാനിലാണ് 🎉`,
    activationSlow:
      "പേയ്മെന്റ് ലഭിച്ചു ✅ കുറച്ച് മിനിറ്റിനുള്ളിൽ പ്ലാൻ ആക്ടീവ് ആകും. അതിനുശേഷം വന്ന് പുതിയ വിവരങ്ങൾ ചേർക്കുക.",
    unlockedIntro: "ഈ പ്ലാനിൽ പുതുതായി ലഭിക്കുന്നത്:",
    unlockedCount: (n, what) => `${n} ${what} കൂടി`,
    unlockedNothing: "പുതുതായി ചേർക്കാൻ ഒന്നുമില്ല — എല്ലാം തയ്യാർ.",
    fillNow: "ഇപ്പോൾ ചേർക്കാം",
    later: "പിന്നീട്",
    upgradeBtn: "പ്ലാൻ അപ്ഗ്രേഡ് ചെയ്യുക",
    /* ── Filling in newly unlocked fields, handed over from the upgrade chat ── */
    focusIntro: "പുതിയ പ്ലാനിൽ ലഭിച്ചവ ചേർക്കാം.",
    focusDone: "പുതിയവ എല്ലാം കഴിഞ്ഞു. വേറെ എന്തെങ്കിലും മാറ്റണോ?",

    labels: {
      ownerName: "നിങ്ങളുടെ പേര്",
      mobile: "മൊബൈൽ",
      bizName: "സ്ഥാപനത്തിന്റെ പേര്",
      type: "തരം",
      category: "വിഭാഗം",
      subCategory: "ഉപവിഭാഗം",
      location: "ലൊക്കേഷൻ",
      district: "ജില്ല",
      postOffice: "സിറ്റി / പോസ്റ്റ് ഓഫീസ്",
      pincode: "പിൻകോഡ്",
      about: "വിവരണം",
      logo: "ലോഗോ",
      banner: "ബാനർ",
      catalogue: "കാറ്റലോഗ് ഫോട്ടോകൾ",
      cover: "കവർ ഫോട്ടോകൾ",
      services: "സേവനങ്ങൾ",
      social: "സോഷ്യൽ ലിങ്കുകൾ",
    },

    /* ── Edit chat (/edit-chat) ── */
    editGreet: "ഹായ് 👋 നിങ്ങളുടെ ലിസ്റ്റിംഗ് അപ്ഡേറ്റ് ചെയ്യാം.",
    loadingBiz: "നിങ്ങളുടെ സ്ഥാപന വിവരങ്ങൾ എടുക്കുന്നു…",
    loadFailed: "വിവരങ്ങൾ ലഭിച്ചില്ല. വീണ്ടും ശ്രമിക്കുക.",
    noAuth: "ആദ്യം സൈൻ ഇൻ ചെയ്യുക, എന്നിട്ട് ലിസ്റ്റിംഗ് തുറക്കാം.",
    signInBtn: "സൈൻ ഇൻ ചെയ്യുക",
    noBusiness: "നിങ്ങൾ ഇതുവരെ സ്ഥാപനം ചേർത്തിട്ടില്ല. ഇപ്പോൾ ചേർക്കണോ?",
    registerBtn: "സ്ഥാപനം രജിസ്റ്റർ ചെയ്യുക",
    menuIntro:
      "ഇതാ നിങ്ങളുടെ ലിസ്റ്റിംഗ്. മാറ്റേണ്ടതിന് നേരെ 'മാറ്റുക' അമർത്തുക.",
    menuAgain: "വേറെ എന്തെങ്കിലും മാറ്റണോ?",
    current: (v) => `ഇപ്പോൾ ഇത് ആണ്: ${v}`,
    currentNone: "ഇത് ഇതുവരെ നൽകിയിട്ടില്ല.",
    saved: "സേവ് ചെയ്തു ✅",
    saveFailed: (m) => `സേവ് ചെയ്യാനായില്ല: ${m || "വീണ്ടും ശ്രമിക്കുക"}`,
    back: "ലിസ്റ്റിലേക്ക് തിരികെ",
    finish: "കഴിഞ്ഞു",
    planNote:
      "ചില ഫീൽഡുകൾ നിങ്ങളുടെ നിലവിലെ പ്ലാനിൽ ഇല്ല, അതിനാൽ അവ ഇവിടെ കാണിക്കുന്നില്ല.",
    catalogueMenuIntro: "നിങ്ങളുടെ കാറ്റലോഗ് ഫോട്ടോകൾ:",
    coverMenuIntro: "നിങ്ങളുടെ കവർ ഫോട്ടോകൾ:",
    serviceMenuIntro: "നിങ്ങളുടെ സേവനങ്ങൾ:",
    socialMenuIntro: "നിങ്ങളുടെ ലിങ്കുകൾ:",
    emptyList: "ഒന്നും ചേർത്തിട്ടില്ല.",
    addNew: "പുതിയത് ചേർക്കുക",
    remove: "നീക്കം ചെയ്യുക",
    removed: "നീക്കം ചെയ്തു ✅",
    confirmRemove: (n) => `${n ? `"${n}"` : "ഇത്"} നീക്കം ചെയ്യണോ?`,
    linkCannotClear:
      "ലിങ്ക് ഒഴിവാക്കാൻ കഴിയില്ല — പുതിയത് അയയ്ക്കുക, അല്ലെങ്കിൽ തിരികെ പോകുക.",
    editDoneTitle: "എല്ലാം അപ്ഡേറ്റ് ചെയ്തു 🎉",
    editDoneBody: "മാറ്റങ്ങൾ നിങ്ങളുടെ പേജിൽ ലൈവ് ആണ്.",
    socialLabels: {
      whatsapp_number: "വാട്സ്ആപ്പ്",
      facebook: "ഫേസ്ബുക്ക്",
      instagram: "ഇൻസ്റ്റാഗ്രാം",
      youtube: "യൂട്യൂബ്",
      website: "വെബ്സൈറ്റ്",
      google_review_link: "ഗൂഗിൾ റിവ്യൂ",
      google_location_link: "ഗൂഗിൾ മാപ്സ്",
    },
  },
};

export default STRINGS;
